import type { Log } from "../log/Log";
import type { Texture } from "pixi.js";
import type { SpriteFontConfigJson } from "./SpriteFontConfig";

export interface GlyphMetrics {
  texture: Texture;
  /** Horizontal advance in pixels (distance cursor moves after this glyph). */
  advanceWidth: number;
  /** Vertical offset from baseline in pixels (authored y-up: positive raises the glyph). */
  offsetY: number;
  /** Natural height of the frame in pixels. */
  height: number;
}

/** Frame-name → texture lookup — a Pixi `Spritesheet.textures` map, or any equivalent. */
export interface GlyphTextureSource {
  getTexture(frameName: string): Texture | null | undefined;
}

export class SpriteFontDefinition {
  private readonly glyphs: Map<string, GlyphMetrics>;
  private readonly fallbackGlyph: GlyphMetrics | null;

  public readonly letterSpacing: number;
  public readonly spaceWidth: number;

  private constructor(
    glyphs: Map<string, GlyphMetrics>,
    fallbackGlyph: GlyphMetrics | null,
    letterSpacing: number,
    spaceWidth: number,
  ) {
    this.glyphs = glyphs;
    this.fallbackGlyph = fallbackGlyph;
    this.letterSpacing = letterSpacing;
    this.spaceWidth = spaceWidth;
  }

  public getGlyph(char: string): GlyphMetrics | null {
    return this.glyphs.get(char) ?? this.fallbackGlyph;
  }

  public hasGlyph(char: string): boolean {
    return this.glyphs.has(char);
  }

  /**
   * Build a runtime font definition from a JSON config + loaded spritesheet textures.
   * Iterates config entries, resolves each frame texture, logs warnings for missing frames.
   */
  public static fromConfig(input: {
    config: SpriteFontConfigJson;
    atlas: GlyphTextureSource | Record<string, Texture>;
    log?: Log;
  }): SpriteFontDefinition {
    const { config } = input;
    const log = input.log ?? (() => {});
    const atlas = input.atlas;
    const lookup = (name: string): Texture | null =>
      (typeof (atlas as GlyphTextureSource).getTexture === "function"
        ? (atlas as GlyphTextureSource).getTexture(name)
        : (atlas as Record<string, Texture>)[name]) ?? null;

    const glyphs = new Map<string, GlyphMetrics>();

    for (const entry of config.glyphs) {
      const char = entry.char;
      if (!char) {
        log("[SpriteFontDefinition] skipping entry with empty char");
        continue;
      }

      const frameName = (entry.frame ?? "").trim();
      if (!frameName) {
        log(`[SpriteFontDefinition] entry "${char}" has empty frame name`);
        continue;
      }

      const texture = lookup(frameName);
      if (!texture) {
        log(`[SpriteFontDefinition] atlas missing frame "${frameName}" for char="${char}"`);
        continue;
      }

      const advanceWidth =
        typeof entry.advance === "number" && entry.advance >= 0 ? entry.advance : texture.orig.width;

      glyphs.set(char, {
        texture,
        advanceWidth,
        offsetY: entry.offsetY ?? 0,
        height: texture.orig.height,
      });
    }

    let fallbackGlyph: GlyphMetrics | null = null;
    if (config.fallbackFrame) {
      const fbFrame = lookup(config.fallbackFrame);
      if (fbFrame) {
        fallbackGlyph = {
          texture: fbFrame,
          advanceWidth: fbFrame.orig.width,
          offsetY: 0,
          height: fbFrame.orig.height,
        };
      } else {
        log(`[SpriteFontDefinition] fallback frame "${config.fallbackFrame}" not found in atlas`);
      }
    }

    log(`[SpriteFontDefinition] built font with ${glyphs.size} glyphs`);

    return new SpriteFontDefinition(glyphs, fallbackGlyph, config.letterSpacing ?? 0, config.spaceWidth ?? 20);
  }
}
